/**
 * Cockpit do Parceiro — /comercial/parceiros/:id (fiel ao mockup fase-3
 * "Detalhe Parceiro").
 *
 * Zona 1: header .eh (avatar gradiente + badges tipo/Ativo/Top parceiro +
 *   documento/email/telefone/"parceiro desde" + WhatsApp + Editar + Novo
 *   orcamento).
 * Zona 2: .kpi-strip-v2 (RT comissao · Indicacoes 30d · Volume gerado ·
 *   Comissao paga · Conversao + card Proxima acao = comissao a pagar).
 * Zona 3: 2 colunas — esquerda: Indicacoes & pedidos (tabela com tabs
 *   Todos/Em curso/Fechados); direita: Dados cadastrais + Pagamento de comissao.
 *
 * Dados vem do endpoint unico GET /api/comercial/representantes/{id}/cockpit.
 * Metricas calculadas AO VIVO no backend (OrcamentoParceiro + Financeiro);
 * parceiro sem vinculo mostra zeros / "—" (sem numero inventado). A edicao
 * reusa o FormPanel compartilhado com a lista.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiEdit2,
  FiExternalLink,
  FiMessageCircle,
  FiPlus,
  FiZap,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { obterCockpitParceiro, obterRepresentante } from "../../../services/comercial";
import { PageContainer } from "../../../styles/shared";

import ParceiroFormPanel from "./FormPanel";
import { CICLO_LABEL, fmtBRLCompacto, REDES_SOCIAIS, TIPO_LABEL, urlRedeSocial } from "./shared";

// ---------- Helpers ----------

function fmtBRLCheio(valor) {
  const n = Number(valor) || 0;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

function avatarGradiente(nome) {
  let h = 0;
  for (const ch of nome || "x") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const a = (h % 8) + 1;
  let b = ((h >>> 3) % 8) + 1;
  if (b === a) b = (b % 8) + 1;
  return `linear-gradient(135deg, var(--chart-${a}), var(--chart-${b}))`;
}

function whatsappUrl(telefone) {
  const digitos = (telefone || "").replace(/\D/g, "");
  return digitos ? `https://wa.me/${digitos}` : null;
}

function prefixoHa(rotulo) {
  if (!rotulo) return "";
  if (rotulo === "hoje" || rotulo === "ontem") return rotulo;
  return `há ${rotulo}`;
}

// Tom (paleta canonica) da situacao do orcamento na tabela de indicacoes.
const SIT_TONE = {
  RASCUNHO: "neutral",
  EM_NEGOCIACAO: "info",
  APROVADO: "info",
  CONVERTIDO: "success",
  RECUSADO: "danger",
  EXPIRADO: "warning",
  CANCELADO: "neutral",
};

const ABAS = [
  ["todos", "Todos"],
  ["em_curso", "Em curso"],
  ["fechados", "Fechados"],
];

function rotuloConversao(pct) {
  if (pct == null) return "sem decididos";
  if (pct >= 60) return "excelente";
  if (pct >= 40) return "boa";
  if (pct >= 20) return "ok";
  return "baixa";
}

// ---------- Sub-componentes ----------

function KpiV2({ label, value, unit, meta, valueStyle }) {
  return (
    <div className="kpi-v2">
      <div className="lbl">{label}</div>
      <div className="val" style={valueStyle}>
        {value}
        {unit ? <span className="unit">{unit}</span> : null}
      </div>
      <div className="meta">{meta}</div>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex between" style={{ gap: 12, alignItems: "baseline" }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{label}</span>
      <span
        className={mono ? "mono" : undefined}
        style={{ fontSize: 13, textAlign: "right", minWidth: 0, wordBreak: "break-word" }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

// ---------- Pagina ----------

export default function ParceiroDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [estado, setEstado] = useState({ loading: true, error: null, cockpit: null });
  const [aba, setAba] = useState("todos");
  const [editando, setEditando] = useState(false);
  const [repEdicao, setRepEdicao] = useState(null);

  const podeEditar = isMaster || hasAllPermissions(["representante:edit"]);
  const podeCriarOrcamento = isMaster || hasAllPermissions(["orcamento:create"]);

  const carregar = useCallback(async () => {
    setEstado({ loading: true, error: null, cockpit: null });
    try {
      const cockpit = await obterCockpitParceiro(request, id, { silentError: true });
      setEstado({ loading: false, error: null, cockpit });
    } catch (err) {
      if (err?.status === 404) {
        setEstado({ loading: false, error: null, cockpit: null, naoEncontrado: true });
      } else {
        setEstado({
          loading: false,
          error: err?.message || "Erro ao carregar parceiro.",
          cockpit: null,
        });
      }
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cockpit = estado.cockpit;
  const parceiro = cockpit?.parceiro;
  const kpis = cockpit?.kpis;
  const indicacoes = useMemo(() => cockpit?.indicacoes || [], [cockpit]);
  const leadsIndicados = useMemo(() => cockpit?.leads_indicados || [], [cockpit]);
  const clientesIndicados = useMemo(
    () => cockpit?.clientes_indicados || [],
    [cockpit],
  );
  const pagamento = cockpit?.pagamento;

  const indicacoesFiltradas = useMemo(() => {
    if (aba === "em_curso") return indicacoes.filter((i) => i.em_curso);
    if (aba === "fechados") return indicacoes.filter((i) => i.fechado);
    return indicacoes;
  }, [indicacoes, aba]);

  const badges = useMemo(() => {
    if (!parceiro) return [];
    const out = [];
    out.push({ label: TIPO_LABEL[parceiro.tipo] || parceiro.tipo_display, tone: "accent", noDot: true });
    out.push(
      parceiro.ativo
        ? { label: "Ativo", tone: "success" }
        : { label: "Inativo", tone: "neutral", noDot: true }
    );
    if (parceiro.top_parceiro) out.push({ label: "Top parceiro", tone: "accent", noDot: true });
    return out;
  }, [parceiro]);

  const abrirWhatsapp = useCallback(() => {
    const url = whatsappUrl(parceiro?.telefone);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [parceiro]);

  // O cockpit traz um `parceiro` enxuto (sem banco/pix/ciclo/comissao/obs).
  // Para editar, busca o Representante completo (como o cockpit do cliente faz).
  const abrirEdicao = useCallback(async () => {
    try {
      const full = await obterRepresentante(request, id);
      setRepEdicao(full);
      setEditando(true);
    } catch {
      /* erro tratado pelo auto-toast do request */
    }
  }, [request, id]);

  // ----- Estados de carregamento / fallback -----

  if (estado.loading) {
    return (
      <PageContainer>
        <div style={{ padding: 24, color: "var(--ink-3)" }}>Carregando parceiro…</div>
      </PageContainer>
    );
  }

  if (estado.naoEncontrado || (!estado.error && !parceiro)) {
    return (
      <PageContainer>
        <div className="so-banner" role="status" style={{ marginBottom: 12 }}>
          Parceiro não encontrado. Pode ter sido removido ou você não tem permissão para vê-lo.
        </div>
        <button type="button" className="btn btn--secondary" onClick={() => navigate("/comercial/parceiros")}>
          <FiArrowLeft size={14} aria-hidden="true" /> Voltar à lista
        </button>
      </PageContainer>
    );
  }

  if (estado.error) {
    return (
      <PageContainer>
        <div className="so-banner danger" role="alert" style={{ marginBottom: 12 }}>{estado.error}</div>
        <button type="button" className="btn btn--secondary" onClick={() => navigate("/comercial/parceiros")}>
          Voltar para a lista
        </button>
      </PageContainer>
    );
  }

  const subParts = [parceiro.documento, parceiro.email, parceiro.telefone].filter(Boolean);
  const desde =
    parceiro.desde_anos > 0
      ? `parceiro há ${parceiro.desde_anos} ${parceiro.desde_anos === 1 ? "ano" : "anos"}`
      : "parceiro recente";
  const waUrl = whatsappUrl(parceiro.telefone);
  const proxima = cockpit?.proxima_acao;
  // View-model da proxima acao por tipo (comissao a pagar / acompanhar orcamento
  // / reativar). Plain const (nao hook) porque vem depois dos early returns.
  const dlabel = (d) => `${d} ${d === 1 ? "dia" : "dias"}`;
  let px = null;
  if (proxima?.tipo === "comissao_a_pagar") {
    px = {
      title: `Comissão de ${fmtBRLCompacto(proxima.valor)} a pagar`,
      ctaLabel: "Ver comissões →",
      onCta: () => navigate("/financeiro/comissoes"),
    };
  } else if (proxima?.tipo === "acompanhar_orcamento") {
    const suf = proxima.dias != null ? ` · há ${dlabel(proxima.dias)}` : "";
    px = {
      title: `Acompanhar ${proxima.orcamento_numero} — em negociação${suf}`,
      ctaLabel: "Abrir orçamento →",
      onCta: () => navigate(`/orcamentos/${proxima.orcamento_id}`),
    };
  } else if (proxima?.tipo === "reativar") {
    px = {
      title: `Sem indicações há ${dlabel(proxima.dias)} — reative o parceiro`,
      ctaLabel: waUrl ? "Chamar no WhatsApp →" : null,
      onCta: waUrl ? abrirWhatsapp : null,
    };
  }

  return (
    <PageContainer style={{ maxWidth: "none" }}>
      {/* Breadcrumb */}
      <div className="breadcrumb" style={{ marginBottom: 8 }}>
        <span className="crumb">Início</span>
        <span className="crumb-sep">/</span>
        <span className="crumb">Comercial</span>
        <span className="crumb-sep">/</span>
        <span className="crumb">Parceiros</span>
        <span className="crumb-sep">/</span>
        <span className="crumb active">{parceiro.nome}</span>
      </div>

      {/* Zona 1 — header */}
      <div className="eh">
        <button className="eh__back" type="button" onClick={() => navigate("/comercial/parceiros")}>
          <FiArrowLeft size={16} aria-hidden="true" />
          Lista
        </button>
        <div
          className="eh__leading"
          style={{ background: avatarGradiente(parceiro.nome), color: "white", border: 0 }}
        >
          {parceiro.iniciais || "?"}
        </div>
        <div className="eh__id">
          <div className="eh__id-top">
            <h2 className="eh__title">{parceiro.nome}</h2>
            {badges.map((b, i) => (
              <span key={i} className={`badge ${b.tone}${b.noDot ? " no-dot" : ""}`}>
                {b.label}
              </span>
            ))}
          </div>
          <div className="eh__sub">
            {subParts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="sep" />}
                <span className="mono">{p}</span>
              </React.Fragment>
            ))}
            {subParts.length > 0 && <span className="sep" />}
            <span>{desde}</span>
          </div>
        </div>
        <div className="eh__actions">
          {waUrl && (
            <button className="btn btn--secondary" type="button" onClick={abrirWhatsapp}>
              <FiMessageCircle size={16} aria-hidden="true" /> WhatsApp
            </button>
          )}
          {podeEditar && (
            <button className="btn btn--secondary" type="button" onClick={abrirEdicao}>
              <FiEdit2 size={16} aria-hidden="true" /> Editar
            </button>
          )}
          {podeCriarOrcamento && (
            <button className="btn btn--primary" type="button" onClick={() => navigate("/orcamentos")}>
              <FiPlus size={16} aria-hidden="true" /> Novo orçamento
            </button>
          )}
        </div>
      </div>

      {/* Zona 2 — KPIs */}
      <div className="kpi-strip-v2">
        <KpiV2
          label="RT comissão"
          value={kpis?.rt_percentual != null ? Math.round(kpis.rt_percentual) : "—"}
          unit={kpis?.rt_percentual != null ? "%" : undefined}
          meta="sobre valor fechado"
        />
        <KpiV2
          label="Indicações 30d"
          value={kpis?.indicacoes_30d ?? 0}
          meta={`${kpis?.indicacoes_total ?? 0} no total`}
        />
        <KpiV2
          label="Volume gerado"
          value={fmtBRLCompacto(kpis?.volume_gerado_30d)}
          meta="últimos 30 dias"
        />
        <KpiV2
          label="Comissão paga"
          value={fmtBRLCompacto(kpis?.comissao_paga_ano)}
          meta="acumulado no ano"
        />
        <KpiV2
          label="Conversão"
          value={kpis?.conversao_percentual != null ? Math.round(kpis.conversao_percentual) : "—"}
          unit={kpis?.conversao_percentual != null ? "%" : undefined}
          meta={rotuloConversao(kpis?.conversao_percentual)}
        />
        {px ? (
          <div className="next-action">
            <div className="lbl">
              <FiZap size={13} aria-hidden="true" /> PRÓXIMA AÇÃO
            </div>
            <div className="title">{px.title}</div>
            {px.ctaLabel && (
              <button
                type="button"
                className="action-link"
                onClick={px.onCta}
                style={{ border: 0, background: "transparent", padding: 0, font: "inherit", cursor: "pointer" }}
              >
                {px.ctaLabel}
              </button>
            )}
          </div>
        ) : (
          <KpiV2
            label="Próxima ação"
            value="Em dia"
            meta="nada pendente"
            valueStyle={{ fontSize: 18, paddingTop: 2 }}
          />
        )}
      </div>

      {/* Zona 3 — 2 colunas */}
      <div className="row-2col" style={{ marginTop: 14 }}>
        {/* Esquerda — Indicações & pedidos */}
        <div className="stack-14">
          <div className="block">
            <div className="block__head">
              <div className="block__title">
                Indicações &amp; pedidos
                <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                  {" "}· {indicacoes.length} {indicacoes.length === 1 ? "indicação" : "indicações"}
                </span>
              </div>
              {indicacoes.length > 0 && (
                <div className="tabchip-group" style={{ height: 26 }}>
                  {ABAS.map(([v, l]) => (
                    <div
                      key={v}
                      className={`tabchip${aba === v ? " active" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setAba(v)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setAba(v);
                        }
                      }}
                    >
                      {l}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="block__body">
              {indicacoes.length === 0 ? (
                <div style={{ padding: "8px 2px", color: "var(--ink-3)", fontSize: 13 }}>
                  Nenhuma indicação ainda. Quando este parceiro indicar um orçamento, ele aparece aqui.
                </div>
              ) : indicacoesFiltradas.length === 0 ? (
                <div style={{ padding: "8px 2px", color: "var(--ink-3)", fontSize: 13 }}>
                  Nenhuma indicação nesta aba.
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="dtable">
                    <colgroup>
                      <col style={{ width: "26%" }} />
                      <col />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "110px" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Orçamento</th>
                        <th>Cliente</th>
                        <th>Situação</th>
                        <th className="num" style={{ textAlign: "right" }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {indicacoesFiltradas.map((i) => (
                        <tr
                          key={i.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(`/orcamentos/${i.id}`)}
                          title="Abrir orçamento"
                        >
                          <td>
                            <span className="mono">{i.numero}</span>
                            {i.rotulo && <div className="tiny muted">{prefixoHa(i.rotulo)}</div>}
                          </td>
                          <td title={i.cliente_nome}>{i.cliente_nome}</td>
                          <td>
                            <span className={`badge ${SIT_TONE[i.situacao] || "neutral"} no-dot`}>
                              {i.situacao_display}
                            </span>
                          </td>
                          <td className="num">{fmtBRLCheio(i.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {(leadsIndicados.length > 0 || clientesIndicados.length > 0) && (
            <div className="block">
              <div className="block__head">
                <div className="block__title">
                  Leads &amp; clientes indicados
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                    {" "}· {leadsIndicados.length}{" "}
                    {leadsIndicados.length === 1 ? "lead ativo" : "leads ativos"} ·{" "}
                    {clientesIndicados.length}{" "}
                    {clientesIndicados.length === 1 ? "cliente" : "clientes"}
                  </span>
                </div>
              </div>
              <div className="block__body">
                {leadsIndicados.length > 0 && (
                  <>
                    <div className="tiny muted" style={{ marginBottom: 6 }}>
                      Leads em andamento
                    </div>
                    <div
                      className="table-wrap"
                      style={{ marginBottom: clientesIndicados.length ? 14 : 0 }}
                    >
                      <table className="dtable">
                        <colgroup>
                          <col style={{ width: "26%" }} />
                          <col />
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "110px" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Lead</th>
                            <th>Título</th>
                            <th>Etapa</th>
                            <th className="num" style={{ textAlign: "right" }}>
                              Estimado
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadsIndicados.map((l) => (
                            <tr
                              key={l.id}
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/crm/leads/${l.id}`)}
                              title="Abrir lead"
                            >
                              <td>
                                <span className="mono">{l.numero}</span>
                                {l.rotulo && (
                                  <div className="tiny muted">{prefixoHa(l.rotulo)}</div>
                                )}
                              </td>
                              <td title={l.titulo}>{l.titulo}</td>
                              <td>{l.etapa || "—"}</td>
                              <td className="num">
                                {l.valor_estimado != null
                                  ? fmtBRLCheio(l.valor_estimado)
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {clientesIndicados.length > 0 && (
                  <>
                    <div className="tiny muted" style={{ marginBottom: 6 }}>
                      Clientes convertidos / atribuídos
                    </div>
                    <div className="table-wrap">
                      <table className="dtable">
                        <colgroup>
                          <col />
                          <col style={{ width: "44%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Cliente</th>
                            <th>Contato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientesIndicados.map((c) => (
                            <tr
                              key={c.id}
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/clientes/${c.id}`)}
                              title="Abrir cliente"
                            >
                              <td title={c.nome}>{c.nome}</td>
                              <td className="mono">
                                {c.email || c.telefone || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Direita — Dados cadastrais + Pagamento */}
        <div className="stack-14">
          <div className="block">
            <div className="block__head">
              <div className="block__title">Dados cadastrais</div>
            </div>
            <div className="block__body">
              <div className="stack" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <InfoRow label="Razão social" value={parceiro.nome} />
                <InfoRow label="CNPJ / CPF" value={parceiro.documento} mono />
                <InfoRow label="Responsável" value={parceiro.responsavel} />
                <InfoRow label="Email" value={parceiro.email} mono />
                <InfoRow label="Telefone" value={parceiro.telefone} mono />
                <InfoRow
                  label="Cidade · UF"
                  value={[parceiro.cidade, parceiro.uf].filter(Boolean).join(" · ")}
                />
                {REDES_SOCIAIS.map((r) => (
                  <InfoRow
                    key={r.key}
                    label={r.label}
                    value={
                      parceiro[r.key] ? (
                        <a
                          href={urlRedeSocial(r.key, parceiro[r.key])}
                          target="_blank"
                          rel="noreferrer"
                          title={`Abrir ${r.label} em nova aba`}
                        >
                          {parceiro[r.key]}
                        </a>
                      ) : ""
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="block">
            <div className="block__head">
              <div className="block__title">Pagamento de comissão</div>
            </div>
            <div className="block__body">
              <div className="stack" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <InfoRow
                  label="RT comissão"
                  value={kpis?.rt_percentual != null ? `${Number(kpis.rt_percentual)}%` : "—"}
                />
                <InfoRow label="Banco" value={pagamento?.banco} />
                <InfoRow label="Chave PIX" value={pagamento?.chave_pix} mono />
                <InfoRow
                  label="Ciclo de pagamento"
                  value={pagamento?.ciclo_pagamento_display || CICLO_LABEL[pagamento?.ciclo_pagamento] || "—"}
                />
                <InfoRow label="Comissão paga (ano)" value={fmtBRLCheio(kpis?.comissao_paga_ano)} />
              </div>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ marginTop: 12 }}
                onClick={() => navigate("/financeiro/comissoes")}
              >
                <FiExternalLink size={14} aria-hidden="true" /> Ver comissões no Financeiro
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SlideOverPanel — editar (compartilhado com a lista) */}
      <ParceiroFormPanel
        open={editando}
        parceiro={repEdicao}
        onClose={() => {
          setEditando(false);
          setRepEdicao(null);
        }}
        onSaved={() => {
          setEditando(false);
          setRepEdicao(null);
          carregar();
        }}
      />
    </PageContainer>
  );
}
